// --------------------- Import Globais ---------------------
import { useState, useEffect, useMemo, useRef } from "react";
import { useMediaQuery } from "@mui/material";
// --------------------- Import Antd-Design ---------------------
import {
  Col,
  Flex,
  Row,
  Button,
  MenuProps,
  Skeleton,
  Table,
  Typography,
  Modal,
} from "antd";
import type { TableProps } from "antd/es/table";
import type { FilterConfirmProps, FilterValue } from "antd/es/table/interface";
import { useMessage } from "../../uteis/mensagens/useMessage";

// --------------------- Import Material Icon ---------------------
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

import {
  Container,
  ContainerItems,
  ContainerLista,
  ContainerSeta,
  Icon,
  Items,
} from "./styles";
import { useBuscarInteresses } from "./hooks/useBuscarInteresses";
import { Columns } from "./components/Columns";
import ModalExcluir from "./components/ModalExcluir";
import dayjs from "dayjs";
import { FiltroMobileModal } from "./components/ModalFiltroMobile";
import ModalAcoes from "./components/ModalAcoes";
import { Interesses } from "../../interfaces/Interesses";
import { validateToken } from "../../components/Globais";
import InfoModal from "./components/InfoModal";

export default function ViewInteresses() {
  // Traz o estilo Title da biblioteca
  const { Title } = Typography;
  // Controle de Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useMediaQuery("(max-width:600px)");

  const usuarioId = Number(validateToken()?.id);

  const { interesses, isLoading, buscar } = useBuscarInteresses();

  const interesseIdCurrent = useRef<number>();

  const [interesseId, setInteresseId] = useState<number>();

  // Inicializando o filtro
  const [filteredInfo, setFilteredInfo] = useState<{
    dataIni?: string[] | null;
    dataFim?: string[] | null;
    situacao?: string[] | null;
    status?: string[] | null;
  }>({
    dataIni: null,
    dataFim: null,
    situacao: null,
    status: null,
  });
  const [semFiltros, setSemFiltros] = useState(true);

  useEffect(() => {
    const filtros = localStorage.getItem("filtrosInteresses");
    const pagina = localStorage.getItem("pageInteresses");

    if (filtros) {
      const novoFiltro = JSON.parse(filtros);
      setFilteredInfo({
        dataIni: novoFiltro.dataIni ?? null,
        dataFim: novoFiltro.dataFim ?? null,
        situacao: novoFiltro.situacao ?? null,
        status: novoFiltro.status ?? null,
      });

      setSemFiltros(false);
    }

    if (pagina) {
      setCurrentPage(Number(pagina));
    }
  }, []);

  const onChange: TableProps<any>["onChange"] = (pagination, filters) => {
    setFilteredInfo({
      dataIni: filters.dataIni as string[] | null,
      dataFim: filters.dataFim as string[] | null,
      situacao: filters.situacao as string[] | null,
      status: filters.status as string[] | null,
    });

    const hasFilters = Boolean(
      filters.dataIni || filters.dataFim || filters.situacao || filters.status
    );
    setSemFiltros(!hasFilters);

    if (pagination.current) {
      localStorage.setItem("pageInteresses", pagination.current.toString());
    }

    localStorage.setItem("filtrosInteresses", JSON.stringify(filters));
  };

  const removerFiltros = () => {
    localStorage.removeItem("filtrosInteresses");
    localStorage.removeItem("pageInteresses");

    setFilteredInfo({
      dataIni: null,
      dataFim: null,
      situacao: null,
      status: null,
    });
    setSemFiltros(true);
    setCurrentPage(1);
  };

  const handleFilter = () => {
    removerFiltros();
  };

  const [modalFiltroAberto, setModalFiltroAberto] = useState<boolean>(false);

  const aplicarFiltroMobile = (valores: Partial<Interesses>) => {
    const filtrosAplicados = {
      dataIni: valores.dataIni
        ? [dayjs(valores.dataIni).format("YYYY-MM-DD")]
        : null,
      dataFim: valores.dataFim
        ? [dayjs(valores.dataFim).format("YYYY-MM-DD")]
        : null,
      situacao: valores.situacao
        ? Array.isArray(valores.situacao)
          ? valores.situacao
          : [valores.situacao]
        : null,
      status: valores.status
        ? Array.isArray(valores.status)
          ? valores.status
          : [valores.status]
        : null,
    };

    setFilteredInfo(filtrosAplicados);
    localStorage.setItem("filtrosInteresses", JSON.stringify(filtrosAplicados));
    setSemFiltros(false);
    setCurrentPage(1);
  };

  const aplicarFiltrosNaLista = (
    lista: any[],
    filtros: Record<string, FilterValue | null>
  ) => {
    let listaFiltrada = [...lista];

    const getSituacaoVisivel = (item: any): string => {
      const dataFim = dayjs(item.dataFim, "DD/MM/YYYY");
      const hoje = dayjs();

      // Se dataFim passou e estava como Alugar ou Trocar => Fechado
      if (
        dataFim.isBefore(hoje, "day") &&
        ["Alugar", "Trocar", "Alugar ou trocar"].includes(item.situacao)
      ) {
        return "fechado";
      }
      // Se dataFim passou e estava como Alugado ou utilizado parcialmente => Fechado parcialmente
      if (
        dataFim.isBefore(hoje, "day") &&
        ["Alugado ou utilizado parcialmente"].includes(item.situacao)
      ) {
        return "fechado_parcialmente";
      }

      return item.situacao;
    };

    // Filtro de Check-in
    if (filtros.dataIni && filtros.dataIni.length > 0) {
      const checkInFilterValue = filtros.dataIni[0] as string;
      listaFiltrada = listaFiltrada.filter((item) =>
        dayjs(item.dataIni, "DD/MM/YYYY").isSame(
          dayjs(checkInFilterValue, "YYYY-MM-DD"),
          "day"
        )
      );
    }

    // Filtro de Check-out
    if (filtros.dataFim && filtros.dataFim.length > 0) {
      const checkOutFilterValue = filtros.dataFim[0] as string;
      listaFiltrada = listaFiltrada.filter((item) =>
        dayjs(item.dataFim, "DD/MM/YYYY").isSame(
          dayjs(checkOutFilterValue, "YYYY-MM-DD"),
          "day"
        )
      );
    }

    // Filtro de Situação
    if (filtros.situacao && filtros.situacao.length > 0) {
      const situacaoSelecionada = filtros.situacao as string[];
      listaFiltrada = listaFiltrada.filter((item) =>
        situacaoSelecionada.includes(item.situacao)
      );
    }
    // Filtro de Status
    if (filtros.status && filtros.status.length > 0) {
      const statusSelecionados = filtros.status as string[];
      listaFiltrada = listaFiltrada.filter((item) =>
        statusSelecionados.includes(item.status)
      );
    }

    return listaFiltrada;
  };

  const onClick = async ({
    key,
    interesseId: interesseIdRecebido,
    dataIni: dataIniRecebido,
    dataFim: dataFimRecebido,
    situacao: situacaoRecebida,
    status: statusRecebido,
  }: any) => {
    if (isMobile) {
      interesseIdCurrent.current = interesseIdRecebido;
    }
    switch (key) {
      // Editar
      case "1":
        window.location.href = `/interessescontrole/?method=1&&userid=${usuarioId}&&intid=${
          interesseId || interesseIdRecebido
        }`;
        break;

      case "2":
        // Excluir
        handleModalExcluir();
        break;
    }
  };

  const columns = Columns(onClick, setInteresseId, {
    dataIni: filteredInfo.dataIni,
    dataFim: filteredInfo.dataFim,
    situacao: filteredInfo.situacao,
    status: filteredInfo.status,
  });

  const [openModalExcluir, setOpenModalExcluir] = useState<boolean>(false);

  const handleModalExcluir = () => {
    setOpenModalExcluir(!openModalExcluir);
  };

  const [openModalInteresse, setOpenModalInteresse] = useState<boolean>(false);

  const handleModalInteresse = () => {
    setOpenModalInteresse(!openModalInteresse);
  };

  const [interesseSelecionado, setInteresseSelecionado] =
    useState<Partial<Interesses> | null>(null);

  const abrirModalInteresse = (row: Partial<Interesses>) => {
    setInteresseSelecionado(row);
    handleModalInteresse();
  };
  const [selectedData, setSelectedData] = useState<Partial<Interesses>>({
    dataIni: null,
    dataFim: null,
    situacao: null,
    status: null,
  });

  const showModalAcoes = (data: Partial<Interesses>) => {
    setSelectedData(data);
    setInteresseSelecionado(data);
    handleModalAcoes();
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Funçao para fechar o modal excluir assinatura
  const handleModalAcoes = () => {
    setIsModalOpen(!isModalOpen);
  };

  const statusIcons: Record<string, JSX.Element> = {
    "Atendido MC": <CheckCircleIcon style={{ color: "green" }} />,
    "Não Atendido": <CancelIcon style={{ color: "red" }} />,
    Aguardando: <HourglassEmptyIcon style={{ color: "orange" }} />,
  };

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : isMobile ? (
        <Container>
          <Flex vertical style={{ overflow: "auto", padding: 15 }}>
            {/* Cabeçalho */}
            <Row align="middle" gutter={16} style={{ marginBottom: 24 }}>
              <Col flex="40px">
                <ContainerSeta>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      window.history.back();
                    }}
                  >
                    <ArrowBackIcon color="primary" />
                  </a>
                </ContainerSeta>
              </Col>

              <Col flex="auto">
                <Flex align="center" justify="center" wrap="wrap">
                  <HomeIcon
                    color="primary"
                    fontSize="large"
                    style={{ marginRight: 8 }}
                  />
                  <Title level={4} style={{ margin: 0, textAlign: "center" }}>
                    Meus Interesses
                  </Title>
                </Flex>
              </Col>
            </Row>

            {/* Botões de ação */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 16 }}
            >
              <Col>
                <Button
                  href={`/interessescontrole/?method=0&&userid=${usuarioId}&&intid=0`}
                  type="primary"
                >
                  + Interesse
                </Button>
              </Col>

              <Col>
                <InfoModal />
              </Col>

              <Col>
                <Button
                  onClick={() => setModalFiltroAberto(true)}
                  type="primary"
                >
                  <FilterAltIcon />
                </Button>
              </Col>
            </Row>
          </Flex>

          <ContainerLista>
            {aplicarFiltrosNaLista(interesses, filteredInfo).map(
              (row: any, index: number) => (
                <Row
                  key={`${row.key}-${index}`}
                  style={{ width: "100%", margin: 0, padding: 0 }}
                >
                  <Col span={24}>
                    <div
                      onClick={() => abrirModalInteresse(row)}
                      style={{
                        width: "100%",
                        borderBottom: "2px solid #C3CDC7",
                        padding: "12px 16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        background: "#fff",
                      }}
                    >
                      {/* Texto com flex grow */}
                      <div
                        style={{
                          flexGrow: 1,
                          overflow: "hidden",
                        }}
                      >
                        <span
                          style={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          De {row.dataIni} a {row.dataFim}
                        </span>
                      </div>

                      {/* Ícone de status centralizado */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center", // centraliza verticalmente
                          justifyContent: "center", // centraliza horizontalmente
                          marginRight: 50,
                        }}
                      >
                        {statusIcons[row.status] || "❔"}
                      </div>

                      <Button
                        type="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          showModalAcoes({
                            interesseId: row.id,
                            dataIni: row.dataIni,
                            dataFim: row.dataFim,
                            situacao: row.situacao,
                            status: row.status,
                          });
                        }}
                        style={{
                          flexShrink: 0,
                          backgroundColor: "#003644",
                          borderColor: "#003644",
                          color: "white",
                          padding: "4px 12px",
                          minWidth: "70px",
                        }}
                      >
                        Ações
                      </Button>
                    </div>
                  </Col>
                </Row>
              )
            )}
          </ContainerLista>

          {interesseSelecionado && (
            <Modal
              zIndex={20}
              open={openModalInteresse}
              onCancel={() => {
                handleModalInteresse();
                setInteresseSelecionado(null);
              }}
              footer={null}
              title={`Interesse`}
            >
              <ContainerItems>
                <Items>
                  <strong>Data:</strong>
                  <Icon>
                    {interesseSelecionado.dataIni} até{" "}
                    {interesseSelecionado.dataFim}
                  </Icon>
                </Items>
                <Items>
                  <strong>Status:</strong>{" "}
                  <Icon>{interesseSelecionado.status}</Icon>
                </Items>
                <Items>
                  <strong>Situação:</strong>{" "}
                  <Icon>{interesseSelecionado.situacao}</Icon>
                </Items>
              </ContainerItems>
            </Modal>
          )}
        </Container>
      ) : (
        <Container>
          <Row>
            <Col span={5}>
              <Flex align="center" justify="center" style={{ marginTop: 15 }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    window.history.back();
                  }}
                >
                  <ArrowBackIcon color="primary" />
                </a>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" justify="center">
                <HomeIcon color="primary" fontSize="large" />
                <Title
                  style={{
                    marginLeft: 10,
                    marginTop: 10,
                    fontSize: isMobile ? "1rem" : "2rem",
                    color: "rgb(0, 54, 68)",
                  }}
                  level={3}
                >
                  Meus Interesses
                </Title>
              </Flex>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Flex
                justify="flex-end"
                align="center"
                style={{
                  height: "auto",
                  width: "90%",
                  gap: "20px",
                }}
              >
                <Button
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "auto",
                  }}
                  href={`/interessescontrole/?method=0&&userid=${usuarioId}&&intid=0`}
                  type="primary"
                >
                  + Interesse
                </Button>
                {semFiltros ? (
                  <Button onClick={handleFilter} type="primary" disabled>
                    <FilterAltIcon />
                  </Button>
                ) : (
                  <Button onClick={handleFilter} type="primary">
                    <FilterAltOffIcon />
                  </Button>
                )}
              </Flex>
            </Col>

            <Col
              span={22}
              style={{
                marginTop: isMobile ? 15 : 15,
                marginBottom: isMobile ? 30 : 50,
              }}
            >
              <Flex
                style={{
                  width: isMobile ? "auto" : "100%",
                  overflow: "auto",
                  marginLeft: 30,
                }}
              >
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={interesses}
                  onChange={onChange}
                  bordered
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    width: isMobile ? "auto" : "100%",
                    maxWidth: isMobile ? "none" : "100%",
                  }}
                  pagination={{ defaultCurrent: currentPage }}
                />
              </Flex>
            </Col>
          </Row>
        </Container>
      )}
      <ModalExcluir
        isModalOpen={openModalExcluir}
        handleFechar={handleModalExcluir}
        fecharModal={handleModalAcoes}
        interesseId={interesseId || interesseIdCurrent.current}
        buscar={buscar}
      />
      <ModalAcoes
        isModalOpen={isModalOpen}
        handleFechar={handleModalAcoes}
        onClick={onClick}
        zIndex={1000}
        data={selectedData}
      />
      <FiltroMobileModal
        visible={modalFiltroAberto}
        onClose={() => setModalFiltroAberto(false)}
        onApply={aplicarFiltroMobile}
        filtros={{
          dataIni:
            filteredInfo?.dataIni?.[0] &&
            typeof filteredInfo.dataIni[0] === "string"
              ? dayjs(filteredInfo.dataIni[0])
              : null,
          dataFim:
            filteredInfo?.dataFim?.[0] &&
            typeof filteredInfo.dataFim[0] === "string"
              ? dayjs(filteredInfo.dataFim[0])
              : null,
          situacao: Array.isArray(filteredInfo?.situacao)
            ? (filteredInfo.situacao as string[])
            : [],
          status: Array.isArray(filteredInfo?.status)
            ? (filteredInfo.status as string[])
            : [],
        }}
      />
    </>
  );
}
